import { routes } from "../../common/api/routes";
import {
  generateIdeasError,
  generateIdeasStreamDone,
  generateIdeasStreamStart,
  generateIdeasStreamDelta,
  generateIdeasSuccess,
  getTerms,
  setTermsSuccess,
  setTermsError,
  getTermsSuccess,
  getTermsError,
  deleteTermsSuccess,
  deleteTermsError,
} from "../actions";
import { eventChannel, END } from "redux-saga";
import { put, take, call } from "redux-saga/effects";
import {
  downloadActionError,
  downloadActionSuccess,
  reorderActionTermError,
  reorderActionTermSuccess,
  startActionPlan,
  startActionPlanSuccess,
  startActionPlanError,
} from "../actions/actionPlan";

export function* generateIdeasSaga(params) {
  try {
    const channel = eventChannel((emit) => {
      routes.getIdeasChat(params.data, {
        onStart: () => emit({ type: "start" }),
        onDelta: (chunk) => emit({ type: "delta", chunk }),
        onDone: () => {
          emit({ type: "done" });
          emit(END);
        },
        onError: (error) => emit({ type: "error", error }),
      }).catch((error) => {
        emit({ type: "error", error });
        emit(END);
      });

      return () => {};
    });

    let contentBuffer = "";
    let emittedIdeasCount = 0;

    const appendStreamContent = (value) => {
      if (value == null) {
        return;
      }

      if (typeof value === "string") {
        contentBuffer += value;
        return;
      }

      if (Array.isArray(value)) {
        value.forEach(appendStreamContent);
        return;
      }

      if (typeof value === "object") {
        if (typeof value.content === "string") {
          contentBuffer += value.content;
          return;
        }

        if (value.content != null) {
          appendStreamContent(value.content);
          return;
        }

        if (typeof value.response === "string") {
          contentBuffer += value.response;
        }
      }
    };

    const parseGeneratedIdeas = (content, { allowFallback = false } = {}) => {
      const normalizedContent = String(content || "").trim();

      if (!normalizedContent) {
        return [];
      }

      const tryParseLines = (text) => {
        const trimmedLines = String(text)
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);

        const parsedItems = [];

        for (const line of trimmedLines) {
          try {
            const item = JSON.parse(line);
            if (item) {
              parsedItems.push(item);
            }
          } catch {
            continue;
          }
        }

        return parsedItems;
      };

      try {
        const parsed = JSON.parse(normalizedContent);

        if (Array.isArray(parsed)) {
          return parsed;
        }

        if (parsed && typeof parsed === "object") {
          if (Array.isArray(parsed.action_steps)) {
            return parsed.action_steps;
          }

          if (Array.isArray(parsed.data)) {
            return parsed.data;
          }

          return [parsed];
        }
      } catch {
        const lineParsed = tryParseLines(normalizedContent);

        if (lineParsed.length) {
          return lineParsed;
        }
      }

      return allowFallback ? [{ content: normalizedContent }] : [];
    };

    const emitNewIdeas = function* (ideas) {
      if (!Array.isArray(ideas) || !ideas.length) {
        return;
      }

      const nextIdeas = ideas.slice(emittedIdeasCount);

      if (!nextIdeas.length) {
        return;
      }

      for (const idea of nextIdeas) {
        yield put(generateIdeasStreamDelta(idea));
      }

      emittedIdeasCount = ideas.length;
    };

    while (true) {
      const message = yield take(channel);
      if (message.type === "start") {
        contentBuffer = "";
        emittedIdeasCount = 0;
        yield put(generateIdeasStreamStart());
      } else if (message.type === "delta") {
        appendStreamContent(message.chunk?.choices?.[0]?.delta?.content);
        const streamedIdeas = parseGeneratedIdeas(contentBuffer);
        yield* emitNewIdeas(streamedIdeas);
      } else if (message.type === "done") {
        const finalIdeas = parseGeneratedIdeas(contentBuffer, { allowFallback: true });
        yield* emitNewIdeas(finalIdeas);
        yield put(generateIdeasSuccess(finalIdeas));
        yield put(generateIdeasStreamDone());
        break;
      } else if (message.type === "error") {
        yield put(generateIdeasError(message.error?.message || "Failed to generate ideas"));
        yield put(generateIdeasStreamDone());
        break;
      }
    }
  } catch (error) {
    yield put(generateIdeasError(error));
  }
}

export function* getTermsSaga(action) {
    yield put(getTerms());
  try {
    const response = yield call(routes.getTerms, action?.data || {});
    yield put(getTermsSuccess(response));
  } catch (error) {
    yield put(getTermsError(error));
  }
}

export function* startActionPlanSaga(action) {
  yield put(startActionPlan());
  try {
    const ideas = Array.isArray(action?.ideas) ? action.ideas : [];
    if (ideas.length) {
      // Legacy clients only — never bulk-create steps; drag-and-drop saves one at a time.
      console.warn(
        "startActionPlanSaga: ignored bulk ideas payload; use beginActionPlanUi on the client",
      );
    }
    yield put(startActionPlanSuccess());
  } catch (error) {
    yield put(startActionPlanError(error?.response?.data || error?.message || "Failed to start action plan"));
  }
}
export function* addTermsSaga(params) {
  try {
    const response = yield call(routes.addUpdateTerm, params.payload, params.id);
    yield put(setTermsSuccess({
      data: response,
      clientId: params.clientId,
      requestId: params.id,
    }));
  } catch (error) {
    yield put(setTermsError(error?.response?.data || error?.message || "Failed to save action step"));
  }
}

export function* deleteTermSaga(params) {
  try {
    const response = yield call(routes.deleteTerm, params.id);
    yield put(deleteTermsSuccess(response));
  } catch (error) {
    yield put(deleteTermsError(error?.response?.data || error?.message || "Failed to save action step"));
  }
}
export function* downloadActionSaga() {
  try {
    const response = yield call(routes.downloadAction);
    
    // Create blob and download
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'action-plan.pdf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(downloadActionSuccess());
  } catch (error) {
    yield put(downloadActionError(error?.response?.data || error?.message || "Failed to download action plan"));
  }
}
export function* reorderTermsSaga(params) {
  try {
    const response = yield call(routes.reorderTermAction, params.payload);
    yield put(reorderActionTermSuccess(response));
  } catch (error) {
    yield put(reorderActionTermError(error?.response?.data || error?.message || "Failed to save action step"));
  }
}
